<template>
  <div class="wrap" v-if="!loading">
    <div class="header">
      <div class="title-wrap">
        <div class="name" v-if="jyCardInfo.card_name">
          {{ jyCardInfo.card_name }}
        </div>
        <div>
          <div class="card-status">{{ orderStatus }}</div>
          <div
            class="card-status-text"
            v-if="jyCardInfo.pre_order_status === 'refunding'"
          >
            退款申请成功，当前权益不可使用
          </div>
        </div>
      </div>

      <div class="header-content" v-if="isMember">
        <template v-if="!canRefund && !canCancelDeduction">
          <div class="desc">
            有效期：{{ cardCreateTime }}-{{ cardExpiryDate }}
            <p>(有效期为{{ cardTime }}天)</p>
          </div>
        </template>
        <template v-else>
          <div class="new-desc">
            有效期至：{{ cardExpiryDate }}
            <div @click="handleToCancelRenewal">
              <span>｜</span>
              <div>{{ actionText }}</div>
              <div class="arrow"></div>
            </div>
          </div>
        </template>
        <div class="btn" @click="handleToPay">去支付账单</div>
      </div>
      <div
        class="refunding-desc"
        v-else-if="jyCardInfo.pre_order_status === 'refunding'"
      >
        <p>该权益费⽤预计会在1～3个⼯作⽇内原路返回，</p>
        <p>退费成功后会短信通知您，请注意查收</p>
      </div>
      <div class="use-rule" @click="handleToUseRule">使用规则</div>
    </div>
    <div class="main">
      <div
        class="main-item"
        v-for="item in jyCardInfo.benefit_info_list"
        :key="item.id"
        :class="{ 'can-draw': jumpLink }"
      >
        <div class="main-head">
          <div class="icon-wrap">
            <img src="@/assets/images/jiaying-icon.png" />
          </div>
          <div class="main-value">
            <div>{{ item.show_title }}</div>
            <p v-if="item.num">X {{ item.num }}张</p>
          </div>
        </div>
        <div class="main-footer" v-if="jumpLink" @click="handleToJumpLink">
          去使用
        </div>
      </div>
    </div>
    <div class="content">
      <HbBenefit
        v-for="item in categoryList"
        :key="item.id"
        :item="item"
        :card="card"
        :allBenefitList="benefitList"
      />
    </div>
    <div class="footer">
      <div class="footer-content">
        <div class="order" @click="handleToOrder">
          <svg-icon iconClass="bj-service-hz" class="svg-icon" />我的订单
        </div>
        <div class="line"></div>
        <div class="service" @click="handleToService">
          <svg-icon iconClass="bj-order-hz" class="svg-icon" />客服中心
        </div>
      </div>
      <div class="service-time">
        <p v-if="jyCardInfo.is_multi_merchant">
          权益服务由顺潮商城平台入驻商家深圳市橡树黑卡网络科技有限公司提供
        </p>
        <p v-else>权益服务由橡树黑卡提供</p>
        <p>
          客服电话：<a href="tel:400-620-9669">400-620-9669</a> ｜
          <a href="tel:400-150-9669">400-150-9669</a
          ><span>(每日7:00-21:00)</span>
        </p>
      </div>
    </div>
    <CancelOrRefundModal
      :show="cancelOrRefundShow"
      @close="cancelOrRefundShow = $event"
      :canCancelDeduction="canCancelDeduction"
      :canRefund="canRefund"
      :jyCardInfo="jyCardInfo"
      @refresh="fetchJiayinDetail"
    />
  </div>
</template>

<script>
import {
  defineComponent,
  onMounted,
  computed,
  ref,
} from "@vue/composition-api";
import dayjs from "dayjs";
import Vue from "vue";

import { useRouter, useStore, useRoute } from "@/components/use/base";
import useMoreCard from "@/use/useMoreCard";
import HbBenefit from "@/views/index/moreCard/visitor/component/common/HbBenefit.vue";
import {
  getJiayinDetail,
  getJiayinBill,
  getJiayinJumpLink,
} from "@/apis/jiayin";
import { getNamespaceBenefit } from "@/apis/home";
import { jsonSafeParse } from "@/utils";
import { PRE_ORDER_STATUS } from "@/constants";

import CancelOrRefundModal from "../JyComponent/CancelOrRefundModal.vue";

const vm = Vue.prototype;

export default defineComponent({
  components: {
    HbBenefit,
    CancelOrRefundModal,
  },
  setup() {
    const jyCardInfo = ref({});
    const loading = ref(true);
    const benefitList = ref([]);
    const cancelOrRefundShow = ref(false);
    const jumpLink = ref("");

    const router = useRouter();
    const route = useRoute();
    const store = useStore();
    const { info } = route.query;

    const { initMoreCard, card, categoryList } = useMoreCard();
    const userInfo = computed(() => store.state.userInfo);
    const namespace = computed(() => store.state.namespace);
    const isiOS = computed(() => store.state.isiOS);
    const packageName = computed(
      () => store.state.userInfo.card_type || store.state.namespaceInfo.package
    );

    // 申请退费
    const canRefund = computed(() => {
      return (
        !!jyCardInfo.value?.refund_status && !!jyCardInfo.value?.self_refund
      );
    });

    // 取消扣费
    const canCancelDeduction = computed(() => {
      return (
        !!jyCardInfo.value?.unsubscribe_status &&
        !!jyCardInfo.value?.self_unsubscribe
      );
    });

    const actionText = computed(() => {
      if (canRefund.value) {
        return "申请退费";
      }
      if (canCancelDeduction.value) {
        return "取消扣费";
      }
    });

    const orderStatus = computed(() => {
      if (isMember.value) return "生效中";
      return PRE_ORDER_STATUS[jyCardInfo.value?.pre_order_status];
    });

    const isMember = computed(() => store.state.isMember);

    const cardCreateTime = computed(() =>
      dayjs(userInfo.value.t_vip_created).format("YYYY.MM.DD")
    );
    const cardExpiryDate = computed(() =>
      dayjs(userInfo.value.card_expiry_date[0]).format("YYYY.MM.DD")
    );

    const cardTime = computed(() => {
      const start = dayjs(userInfo.value.t_vip_created);
      const end = dayjs(userInfo.value.card_expiry_date[0]);
      return end.diff(start, "day") + 1;
    });

    const handleToCancelRenewal = () => {
      cancelOrRefundShow.value = true;
    };

    const handleToOrder = () => {
      router.push("/order");
    };
    const handleToService = () => {
      router.push("/help");
    };

    const handleToJumpLink = () => {
      window.location.href = jumpLink.value;
    };

    const handleToPay = async () => {
      if (jyCardInfo.value.installment) {
        router.push("/jiayinPayPlan?orderId=" + store.state.orderId);
        return;
      }
      const fetching = vm.$loading();
      try {
        const { data } = await getJiayinBill({
          orderId: store.state.orderId,
        });
        const deductIndex = data?.billOrderItems?.[0]?.deductIndex;
        if (deductIndex) {
          router.push(
            `/jiayinPay?orderId=${store.state.orderId}&indexList=${deductIndex}`
          );
        } else {
          vm.$toast("没有需要支付的账单");
        }
      } finally {
        fetching.clear();
      }
    };

    const handleToUseRule = async () => {
      router.push(`/jiayinRule?orderId=${store.state.orderId}`);
    };

    const fetchJiayinDetail = async () => {
      loading.value = true;
      if (!isMember.value) {
        const { data: benefitData } = await getNamespaceBenefit({
          package: packageName.value,
          namespace: namespace.value,
        });
        benefitList.value = benefitData.map((item) => item.benefit_type);
      }
      try {
        const { data } = await getJiayinDetail({
          orderId: store.state.orderId,
          packageCode: packageName.value || namespace.value.toUpperCase(),
          info: info || sessionStorage.getItem("JY_INFO"),
        });
        console.log(data);
        jyCardInfo.value = data || {};
        const canJumpLink = vm.$getConfigValue("JY_USE_CARD_LINK") || false;
        if (canJumpLink) {
          const { data: jumpLinkData } = await getJiayinJumpLink({
            orderId: store.state.orderId,
            systemType: isiOS.value ? "789" : "799",
          });
          jumpLink.value = jumpLinkData?.jumpCouponUrl || "";
        }
      } finally {
        loading.value = false;
      }
    };

    onMounted(async () => {
      const fetching = vm.$loading();
      const jyInfo = sessionStorage.getItem("JY_INFO");
      if (info && jyInfo !== info) {
        sessionStorage.setItem("JY_INFO", info);
      }
      try {
        const categoryType = jsonSafeParse(
          vm.$getConfigValue("JIAYIN_CATEGORY_MAP")
        )[namespace.value];
        await initMoreCard(categoryType);
        await fetchJiayinDetail();
      } finally {
        loading.value = false;
        fetching.clear();
      }
    });

    return {
      handleToOrder,
      handleToService,
      handleToPay,
      handleToUseRule,
      handleToCancelRenewal,
      fetchJiayinDetail,
      categoryList,
      card,
      jyCardInfo,
      cardCreateTime,
      cardExpiryDate,
      cardTime,
      loading,
      benefitList,
      isMember,
      canRefund,
      canCancelDeduction,
      cancelOrRefundShow,
      actionText,
      orderStatus,
      jumpLink,
      handleToJumpLink,
    };
  },
});
</script>

<style lang="less" scoped>
.wrap {
  min-height: 100vh;
  background: #fff;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.header {
  overflow: hidden;
  height: 148px;
  background-image: url("../../../../../assets/images/new-jiayin-header.png");
  background-size: 100% 100%;
  background-repeat: no-repeat;
  position: relative;

  .title-wrap {
    display: flex;
    align-items: center;
    position: absolute;
    top: 45px;
    left: 44px;
    .name {
      font-family: AlibabaPuHuiTi, AlibabaPuHuiTi;
      font-size: 24px;
      color: #e7dcd5;
      line-height: 16px;
      font-weight: bold;
    }

    .card-status {
      width: fit-content;
      margin-left: 4px;
      padding: 1px 5px;
      background: linear-gradient(123deg, #ffe3b7 0%, #ffdca7 100%);
      border-radius: 1px 6px 1px 6px;
      opacity: 0.89;
      font-weight: 400;
      font-size: 9px;
      color: #1d191c;
      text-align: center;
    }

    .card-status-text {
      font-weight: 400;
      font-size: 6px;
      color: #b8aba4;
      line-height: 8px;
      margin-top: 2px;
    }
  }

  .header-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: absolute;
    // top: 113px;
    top: 89px;
    left: 44px;
    right: 49px;

    .desc {
      font-family: PingFangSC, PingFang SC;
      font-weight: 400;
      font-size: 9px;
      color: #b8aba4;
      line-height: 13px;
    }

    .new-desc {
      font-family: PingFangSC, PingFang SC;
      font-weight: 400;
      font-size: 9px;
      color: #b8aba4;
      display: flex;
      align-items: center;

      > div {
        display: flex;
        align-items: center;
      }
      span {
        margin-left: 4px;
        margin-right: 4px;
      }

      .arrow {
        margin-left: 4px;
        width: 0;
        height: 0;
        border-top: 4px solid transparent;
        border-bottom: 4px solid transparent;
        border-left: 6px solid #b8aba4; /* 颜色在这里改 */
      }
    }

    .btn {
      width: 82px;
      height: 24px;
      background: linear-gradient(123deg, #ffe3b7 0%, #ffdca7 100%);
      border-radius: 13px;
      font-weight: 500;
      font-size: 12px;
      color: #000000;
      line-height: 24px;
      text-align: center;
    }
  }

  .refunding-desc {
    font-weight: 400;
    font-size: 9px;
    color: #b8aba4;
    line-height: 13px;
    position: absolute;
    top: 76px;
    left: 44px;
    right: 49px;
  }

  .use-rule {
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    top: 53px;
    right: 7px;
    width: 17px;
    height: 53px;
    background: linear-gradient(134deg, #3f3631 0%, #211c20 47%, #211c20 100%),
      linear-gradient(134deg, #26180f 0%, #211c20 47%, #020300 100%);
    box-shadow: inset 0px 1px 3px 0px rgba(255, 255, 255, 0.5);
    border-radius: 0 4px 4px 0;

    font-weight: 400;
    font-size: 9px;
    color: rgba(184, 171, 164, 0.63);
    line-height: 10px;
    text-align: center;
  }
}

.main {
  display: flex;
  align-items: center;
  overflow-y: auto;
  padding: 12px;
  margin-bottom: -20px;
  &::-webkit-scrollbar {
    display: none;
  }

  .main-item {
    flex-shrink: 0;
    width: 171px;
    height: 55px;
    background-image: url("../../../../../assets/images/jiaying-card.png");
    background-size: 100% 100%;
    background-repeat: no-repeat;

    &.can-draw {
      background-image: url("../../../../../assets/images/jiaying-card-draw.png");
      height: 81px;
    }

    &:not(:last-child) {
      margin-right: 8px;
    }
  }

  .main-head {
    display: flex;
    align-items: center;
    margin-top: 10px;
    margin-left: 16px;

    .icon-wrap {
      width: 36px;
      height: 36px;
      background: rgba(255, 255, 255, 0.86);
      backdrop-filter: blur(10px);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;

      img {
        width: 30px;
        height: 30px;
      }
    }

    .main-value {
      margin-left: 22px;
      height: 31px;

      > div {
        width: 70px;
        font-family: PingFangSC, PingFang SC;
        font-weight: 600;
        font-size: 12px;
        color: #322e29;
        margin-top: 2px;
        line-height: 17px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      > p {
        font-weight: 400;
        font-size: 10px;
        color: #666666;
        line-height: 12px;
        margin-top: 2px;
      }
    }
  }

  .main-footer {
    font-weight: 500;
    font-size: 12px;
    color: #e9dcc1;
    line-height: 17px;
    text-align: center;
    margin-top: 13px;
  }
}

.footer {
  margin-top: auto;
  padding: 20px 12px;
  .footer-content {
    height: 32px;
    background: #f8f8f8;
    border-radius: 8px;
    display: flex;
    align-items: center;
    font-size: 15px;
    color: #333333;
  }
  .svg-icon {
    font-size: 20px;
    margin-right: 9px;
  }

  .order {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
  }

  .line {
    width: 1px;
    height: 23px;
    background: #e4e4e4;
  }

  .service {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
  }

  .service-time {
    font-weight: 400;
    font-size: 10px;
    color: #666666;
    line-height: 14px;
    text-align: center;
    margin-top: 12px;
    a {
      color: #0170fe;
    }
    p {
      &:last-child {
        color: #0170fe;
      }
      span {
        margin-left: 8px;
        color: #666666;
      }
    }
  }
}
</style>
